/**
 * Normalizes contact-shaped data for created_by and similar API fields.
 * Backend may send a user with nested `contact`; tables should show contact info only.
 */

function pickRelationName(value) {
  if (value == null) return null;
  if (typeof value === "object" && typeof value.name === "string" && value.name.trim()) {
    return value.name.trim();
  }
  if (typeof value === "string" && value.trim()) return value.trim();
  return null;
}

function pickCategory(contact) {
  if (contact.category_name != null && String(contact.category_name).trim() !== "") {
    return String(contact.category_name).trim();
  }
  return pickRelationName(contact.category);
}

function pickCompany(contact) {
  if (contact.company_name != null && String(contact.company_name).trim() !== "") {
    return String(contact.company_name).trim();
  }
  return pickRelationName(contact.company);
}

/**
 * @param {object|null|undefined} raw Contact object (or subset from API).
 * @returns {{ first_name: string|null, last_name: string|null, email: string|null, category: string|null, company: string|null }|null}
 */
export function serializeSimplifiedContact(raw) {
  if (raw == null || typeof raw !== "object") return null;

  const email =
    raw.email != null && String(raw.email).trim() !== "" ? String(raw.email).trim() : null;

  return {
    first_name: raw.first_name ?? null,
    last_name: raw.last_name ?? null,
    email,
    category: pickCategory(raw),
    company: pickCompany(raw),
  };
}

/**
 * Resolves created_by from API: prefers `user.contact`, falls back to contact-shaped payload.
 * @param {object|null|undefined} createdBy
 * @returns {ReturnType<typeof serializeSimplifiedContact>}
 */
export function resolveCreatedByContact(createdBy) {
  if (createdBy == null || typeof createdBy !== "object") return null;
  const source = createdBy.contact != null ? createdBy.contact : createdBy;
  return serializeSimplifiedContact(source);
}

/**
 * @param {ReturnType<typeof serializeSimplifiedContact>} simplified
 * @param {{ fallback?: string }} [options]
 */
export function formatSimplifiedContactDisplay(simplified, options = {}) {
  const { fallback = "" } = options;
  if (!simplified) return fallback;

  const name = [simplified.first_name, simplified.last_name]
    .filter((part) => part != null && String(part).trim() !== "")
    .join(" ")
    .trim();

  if (name) return name;
  if (simplified.email) return simplified.email;
  return fallback;
}

/**
 * Display string for table "Created by" columns.
 * Prefer importing from `tableDisplayUtils` in table UIs.
 * @param {object|null|undefined} createdBy
 * @param {string} [fallback]
 */
export function formatCreatedByDisplay(createdBy, fallback = "") {
  const display = formatSimplifiedContactDisplay(resolveCreatedByContact(createdBy), {
    fallback: "",
  });
  return display || fallback;
}
